import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, getcountries } from '../../../services/admin/accommodationStatus/accommodationStatusServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

const FreeAccommodation = () => {
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // Filter States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [dropdownData, setDropdownData] = useState({
    countries: []
  });

  // Fetch data list
  const fetchData = useCallback(async () => {
    try {
        const data = await getData({ accommodation_status: 'free' });
        setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

// Fetch all dropdown data
  const fetchDropdownData = useCallback(async () => {
    try {
      const [country] = await Promise.all([
        getcountries()
      ]);
  
      setDropdownData({
        countries: country
      });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true); 
      await fetchData();
      await fetchDropdownData();
      // Optional 2 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false); 
    };
  
    fetchDataAsync();
  }, [fetchData, fetchDropdownData]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  //based country code append country name
  const getCountryName = (code) => {
  const country = dropdownData.countries.find(c => c.code === code);
  return country ? country.name : code;
  };

  //filtered data
  const filteredData = useMemo(() => {
  const startDateObj = startDate ? new Date(startDate) : null;
  const endDateObj = endDate ? new Date(endDate) : null;
  
      return dataList
        .filter((data) => {
          const matchesText =
             data.billing_full_name.toLowerCase().includes(searchText.toLowerCase()) ||
            data.sub_domain.toLowerCase().includes(searchText.toLowerCase()) ||
            data.reg_amount.toString().includes(searchText.toLowerCase()) ||
            data.date_of_payment.toString().includes(searchText.toLowerCase())
        
            const date = data.date_of_payment ? new Date(data.date_of_payment) : null;
  
          // If both startDate and endDate are provided, filter by date range
          const inRange = (startDateObj && endDateObj) 
            ? date >= startDateObj && date <= endDateObj 
            : true; // Otherwise, no date filtering
  
          // Other filters (series, subDomain, country)
          const matchSeries = !seriesFilter || data.series === seriesFilter;
          const matchSubDomain = !subDomainFilter || data.sub_domain === subDomainFilter;
          const matchCountry = !countryFilter || data.billing_countries === countryFilter;
  
          return matchesText && inRange && matchSeries && matchSubDomain && matchCountry;
        }).sort((a, b) => new Date(b.date_of_payment) - new Date(a.date_of_payment));
  }, [dataList, searchText, seriesFilter, subDomainFilter, countryFilter, startDate, endDate]);

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '10%',
    },
    {
      name: 'Date of Payment',
      selector: (row) => row.date_of_payment,
      sortable: true,
      width: '10%',
    },
    {
      name: 'Series',
      selector: (row) => row.series,
      sortable: true,
      width: '10%',
    },
    {
      name: 'Sub domain',
      selector: (row) => row.sub_domain,
      sortable: true,
      width: '15%',
      wrap:true,
    },
    {
      name: 'Billing Name',
      selector: (row) => row.billing_full_name,
      sortable: true,
      width: '15%',
      wrap:true,
    },
    {
      name: 'Country',
      selector: (row) => getCountryName(row.billing_countries),
      sortable: true,
      width: '15%',
      wrap:true,

    },
    {
      name: 'Reg Amount',
      selector: (row) => row.reg_amount,
      sortable: true,
      cell: (row) => {
        let currencySymbol = '';
    
        switch (row.currency_type.toUpperCase()) {
            case 'INR':
              currencySymbol = '₹';
              break;
            case 'USD':
              currencySymbol = '$';
              break;
            case 'EUR':
            currencySymbol = '€';
            break;
            case 'GBP':
            currencySymbol = '£';
            break;
            case 'RUB':
            currencySymbol = '₽';
            break;
            case 'THB':
            currencySymbol = '฿';
            break;
            default:
              currencySymbol = row.currency; // Fallback to displaying the currency code if unknown
          }
    
        return (
          <span>
            <b>{currencySymbol} {row.reg_amount}</b>
          </span>
        );
      },
      width: '10%',
      wrap:true,

    },
    {
      name: 'Status',
      selector: (row) => row.accommodation_status,
      sortable: true,
      cell: (row) => (
        <span className="bg-success rounded text-center text-white px-3 py-1">
          {row.accommodation_status}
        </span>
      ),
      width: '10%',
      wrap:true,
    },
  ];

  // Function to render expandable row details
  const ExpandableRow = ({ data }) => {
    return (
      <div className="p-3 bg-light border">
        <table className="table table-bordered">
          <tbody>
            <tr>
              <td><b>Reg Category:</b></td>
              <td>{data.reg_category}</td>
            </tr>
            <tr>
              <td><b>No of Ticket:</b></td>
              <td>{data.no_of_ticket}</td>
            </tr>
            <tr>
              <td><b>Occupancy Type:</b></td>
              <td>{data.occupancy_type}</td>
            </tr>
            <tr>
              <td><b>Total No of Nights:</b></td>
              <td>{data.total_no_of_nights}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        <div className=" row m-3 d-flex flex-column gap-3 ">
          <div className="d-flex gap-2">
            {/* Dropdown for Filters */}
            <div className="col-md-2">
              <span className='fs-5 text-dark'>Series</span>
              <select className="form-control" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)}>
                <option value="">All Series</option>
                {Array.from(new Set(dataList.map(item => item.series))).sort((a, b) => b.localeCompare(a)).map((series, idx) => (
                  <option key={idx} value={series}>{series}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span className='fs-5 text-dark'>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => setSubDomainFilter(e.target.value)}>
                <option value="">All Subdomains</option>
                {Array.from(new Set(dataList.map(item => item.sub_domain))).sort((a, b) => a.localeCompare(b)).map((subDomain, idx) => (
                  <option key={idx} value={subDomain}>{subDomain}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
            <span className='fs-5 text-dark'>Countries</span>
            <select
              className="form-control"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <option value="">All Countries</option>
              {dropdownData.countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

            {/* Start Date and End Date Input */}
            <div className="col-md-2">
              <span className='fs-5 text-dark'>Start Date</span>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <span className='fs-5 text-dark'>End Date</span>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center w-100"
            style={{ minHeight: "300px" }}
          >
            <Spinner/>
          </div>
                  ) :(
        <div>
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-8">
              <p className='fs-4 text-dark'>Conference Management / Accommodation Status / free</p>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            search
            highlightOnHover
            responsive
            striped
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
            onChangePage={handlePageChange}
            subHeader
            subHeaderComponent={
              <div>
                <input className="form-control" type="text" placeholder="Search" value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
            }
            expandableRows
            expandableRowsComponent={ExpandableRow}  // ✅ Adding expandable row
          />
        </div>
        <Footer />
        </div>
        )};
      </div>
    </div>
  );
};

export default FreeAccommodation;
